import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Mic, Square, FileText, Paperclip, ChevronDown, MessageSquarePlus, Play, Pause, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { presignUrl, openS3File } from '@/lib/storage';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Cie11Search, type Cie11Result } from '@/components/cie11/Cie11Search';
import { getErrorMessage } from '@/lib/errors';
import { useAuthStore } from '@/store/auth.store';

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

const TIPO_BADGE: Record<string, string> = {
  texto:               'bg-blue-50 text-blue-600 border-blue-100',
  voz:                 'bg-purple-50 text-purple-600 border-purple-100',
  grabacion_consulta:  'bg-rose-50 text-rose-600 border-rose-100',
};

const TIPO_LABEL: Record<string, string> = {
  texto:              '📝 texto',
  voz:                '🎙 voz',
  grabacion_consulta: '🎙 consulta',
};

function fmtDate(iso: string) {
  try { return format(new Date(iso), "d MMM yyyy 'a las' HH:mm", { locale: es }); }
  catch { return iso; }
}

function AudioPlayer({ s3Url }: { s3Url: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  useEffect(() => {
    presignUrl(s3Url).then(setSignedUrl).catch(() => setSignedUrl(null));
  }, [s3Url]);

  function toggle() {
    if (!audioRef.current || !signedUrl) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  }

  return (
    <div className="flex items-center gap-2 mt-2">
      <button type="button" onClick={toggle} disabled={!signedUrl}
        className="flex items-center gap-1.5 bg-purple-50 border border-purple-100 text-purple-600 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-purple-100 transition-colors disabled:opacity-40">
        {playing ? <Pause size={12} /> : <Play size={12} />}
        {!signedUrl ? 'Cargando...' : playing ? 'Pausar' : 'Reproducir'}
      </button>
      {signedUrl && (
        <audio ref={audioRef} src={signedUrl} onEnded={() => setPlaying(false)} className="hidden" />
      )}
    </div>
  );
}

function VoiceRecorder({ onStop }: { onStop: (blob: Blob) => void }) {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const mrRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mrRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        onStop(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start(200);
      setRecording(true);
      setSecs(0);
      intervalRef.current = setInterval(() => setSecs(s => s + 1), 1000);
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo acceder al micrófono'));
    }
  }

  function stop() {
    mrRef.current?.stop();
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRecording(false);
  }

  const mmss = `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;

  return recording ? (
    <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
      <span className="text-sm text-purple-700 font-medium">{mmss}</span>
      <button type="button" onClick={stop}
        className="ml-auto flex items-center gap-1.5 bg-white border border-purple-200 text-purple-700 px-3 py-1.5 rounded-lg text-xs font-medium">
        <Square size={11} /> Detener
      </button>
    </div>
  ) : (
    <button type="button" onClick={start}
      className="flex items-center gap-2 border border-purple-200 text-purple-600 bg-purple-50 hover:bg-purple-100 px-3 py-2 rounded-xl text-sm font-medium transition-colors">
      <Mic size={14} /> Grabar nota de voz
    </button>
  );
}

interface Props { id: string; canEdit: boolean; noDelete?: boolean; triggerAdd?: number }

export function NotasPacienteTab({ id, canEdit, triggerAdd }: Props) {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => { if (triggerAdd) setModalOpen(true); }, [triggerAdd]);
  const [tipo, setTipo] = useState<'texto' | 'voz'>('texto');
  const [contenido, setContenido] = useState('');
  const [cie11Nota, setCie11Nota] = useState<Cie11Result | null>(null);
  const [isConfidencial, setIsConfidencial] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [adendaOpenId, setAdendaOpenId] = useState<string | null>(null);
  const [adendaTexto, setAdendaTexto] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: notas = [], isLoading } = useQuery({
    queryKey: ['notas-paciente', id],
    queryFn: () => api.get(`/api/pacientes/${id}/notas`).then(r => r.data as any[]),
  });

  const createMutation = useMutation({
    mutationFn: (fd: FormData) => api.post(`/api/pacientes/${id}/notas`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notas-paciente', id] }); toast.success('Nota guardada'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar la nota')),
  });

  const adendaMutation = useMutation({
    mutationFn: ({ notaId, texto }: { notaId: string; texto: string }) =>
      api.post(`/api/pacientes/${id}/notas/${notaId}/adenda`, { texto, autorNombre: usuario?.nombre }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notas-paciente', id] });
      toast.success('Adenda agregada');
      setAdendaOpenId(null);
      setAdendaTexto('');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al agregar la adenda')),
  });

  function openAdd() { setContenido(''); setCie11Nota(null); setAudioBlob(null); setAdjuntos([]); setTipo('texto'); setIsConfidencial(false); setModalOpen(true); }
  function closeModal() { setModalOpen(false); setCie11Nota(null); setAudioBlob(null); setAdjuntos([]); setIsConfidencial(false); }

  function toggleAdenda(notaId: string) {
    setAdendaOpenId(prev => (prev === notaId ? null : notaId));
    setAdendaTexto('');
  }

  function submitAdenda(notaId: string) {
    if (!adendaTexto.trim()) return toast.error('Escribe el texto de la adenda');
    adendaMutation.mutate({ notaId, texto: adendaTexto.trim() });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (tipo === 'texto' && !contenido.trim()) return toast.error('Escribe el contenido de la nota');
    if (tipo === 'voz' && !audioBlob) return toast.error('Graba una nota de voz');

    const fd = new FormData();
    fd.append('tipo', tipo);
    if (contenido) fd.append('contenido', contenido);
    if (cie11Nota?.theCode)  fd.append('codigoCie11',    cie11Nota.theCode);
    if (cie11Nota?.title)    fd.append('tituloCie11',    cie11Nota.title);
    if (cie11Nota?.id)       fd.append('idEntidadCie11', cie11Nota.id);
    if (tipo === 'voz' && audioBlob) fd.append('audio', audioBlob, 'nota-voz.webm');
    fd.append('isConfidencial', String(isConfidencial));
    adjuntos.forEach(f => fd.append('archivos', f, f.name));
    createMutation.mutate(fd);
  }

  function handleAdjunto(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    setAdjuntos(prev => [...prev, ...files]);
    e.target.value = '';
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-text-secondary">
          <span className="font-semibold text-text-primary">{notas.length}</span> nota{notas.length !== 1 ? 's' : ''}
        </p>
        {canEdit && (
          <button type="button" onClick={openAdd}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors">
            <Plus size={13} /> Nueva nota
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>
      ) : notas.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <FileText size={28} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-text-secondary">Sin notas registradas</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notas.map((n: any) => {
            const isExpanded = expandedId === n.id;
            const isLocked = !!n.contenidoCensurado;
            const adjuntosArr: string[] = n.archivosAdjuntos || [];
            return (
              <div key={n.id} className={cn(
                'border rounded-xl overflow-hidden',
                isLocked ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-100',
                n.isConfidencial && 'border-l-4 border-l-rose-400',
              )}>
                <div className="flex items-start gap-3 p-3.5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn('text-[10px] font-medium border px-1.5 py-0.5 rounded-full', TIPO_BADGE[n.tipo] || TIPO_BADGE.texto)}>
                        {TIPO_LABEL[n.tipo] ?? '📝 texto'}
                      </span>
                      <span className="text-[11px] text-text-secondary">{fmtDate(n.createdAt)}</span>
                      {n.medico && <span className="text-[11px] text-text-secondary">· Dr. {n.medico.nombre}</span>}
                      {n.isConfidencial && (
                        <span className="flex items-center gap-1 text-[10px] font-medium text-rose-600 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded-full">
                          <Lock size={9} /> Confidencial
                        </span>
                      )}
                    </div>
                    {isLocked ? (
                      <p className="flex items-center gap-1.5 text-sm text-gray-400 italic">
                        <Lock size={12} /> {n.contenido}
                      </p>
                    ) : (
                      <>
                        {n.tipo === 'voz' && n.audioUrl && (
                          <AudioPlayer s3Url={n.audioUrl} />
                        )}
                        {n.contenido && (
                          <>
                            {n.tipo === 'voz' && (
                              <p className="text-[10px] font-medium text-text-secondary uppercase tracking-wide mt-2 mb-0.5">Transcripción</p>
                            )}
                            <p className={cn('text-sm text-text-primary whitespace-pre-wrap', !isExpanded && n.contenido.length > 180 && 'line-clamp-3')}>
                              {n.contenido}
                            </p>
                            {n.contenido.length > 180 && (
                              <button type="button" onClick={() => setExpandedId(isExpanded ? null : n.id)}
                                className="text-xs text-primary-600 hover:underline mt-1 flex items-center gap-1">
                                {isExpanded ? 'Ver menos' : 'Ver más'} <ChevronDown size={11} className={cn(isExpanded && 'rotate-180')} />
                              </button>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {!isLocked && adjuntosArr.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {adjuntosArr.map((url, i) => (
                          <button key={i} type="button"
                            onClick={() => openS3File(url).catch((err: any) => toast.error(getErrorMessage(err, 'No se pudo abrir el adjunto')))}
                            className="flex items-center gap-1 text-xs bg-gray-100 text-text-secondary hover:bg-gray-200 px-2 py-1 rounded-lg transition-colors">
                            <Paperclip size={10} /> Adjunto {i + 1}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Adendas — aclaraciones posteriores, la nota original nunca se modifica (NOM-004) */}
                    {(n.adendas || []).length > 0 && (
                      <div className="mt-2.5 pl-3 border-l-2 border-amber-300 space-y-2">
                        {n.adendas.map((a: any, i: number) => (
                          <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-[10px] font-semibold text-amber-700">Adenda</span>
                              <span className="text-[10px] text-amber-600">{fmtDate(a.fecha)}</span>
                              {a.autorNombre && <span className="text-[10px] text-amber-600">· {a.autorNombre}</span>}
                            </div>
                            <p className="text-xs text-amber-900 whitespace-pre-wrap">{a.texto}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {adendaOpenId === n.id && (
                      <div className="mt-2.5 space-y-2">
                        <textarea rows={2} autoFocus className={`${inputCls} resize-none`}
                          placeholder="Escribe la aclaración a agregar..."
                          value={adendaTexto} onChange={e => setAdendaTexto(e.target.value)} />
                        <div className="flex gap-2 justify-end">
                          <button type="button" onClick={() => toggleAdenda(n.id)}
                            className="text-xs text-text-secondary px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
                            Cancelar
                          </button>
                          <button type="button" onClick={() => submitAdenda(n.id)} disabled={adendaMutation.isPending}
                            className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 disabled:opacity-60">
                            {adendaMutation.isPending ? 'Guardando...' : 'Guardar adenda'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {canEdit && !isLocked && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button type="button" onClick={() => toggleAdenda(n.id)} title="Agregar adenda"
                        className="p-1.5 rounded-lg hover:bg-amber-50 text-text-secondary hover:text-amber-600 transition-colors">
                        <MessageSquarePlus size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-semibold text-text-primary">Nueva nota</h3>
              <button type="button" onClick={closeModal} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary"><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="flex gap-2">
                {(['texto', 'voz'] as const).map(t => (
                  <button key={t} type="button" onClick={() => setTipo(t)}
                    className={cn('flex-1 py-2 rounded-xl text-sm font-medium border transition-colors', tipo === t ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-200 text-text-secondary hover:border-gray-300')}>
                    {t === 'texto' ? '📝 Texto' : '🎙 Voz'}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1.5">Diagnóstico relacionado <span className="font-normal">(opcional)</span></label>
                <Cie11Search value={cie11Nota} onChange={setCie11Nota} placeholder="Buscar diagnóstico CIE-11..." />
              </div>

              <button type="button" onClick={() => setIsConfidencial(v => !v)}
                className={cn(
                  'w-full flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 text-left transition-colors',
                  isConfidencial ? 'bg-rose-50 border-rose-200' : 'border-gray-200 hover:border-gray-300',
                )}>
                <span className={cn(
                  'flex-shrink-0 w-9 h-5 rounded-full relative transition-colors',
                  isConfidencial ? 'bg-rose-500' : 'bg-gray-300',
                )}>
                  <span className={cn(
                    'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform',
                    isConfidencial ? 'translate-x-[18px]' : 'translate-x-0.5',
                  )} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className={cn('flex items-center gap-1.5 text-sm font-medium', isConfidencial ? 'text-rose-700' : 'text-text-primary')}>
                    <Lock size={13} /> Marcar como nota confidencial
                  </span>
                  <span className="block text-[11px] text-text-secondary mt-0.5">
                    Salud Mental / Sensible — solo tú podrás ver el contenido completo
                  </span>
                </span>
              </button>

              {tipo === 'texto' ? (
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Contenido *</label>
                  <textarea rows={5} className={`${inputCls} resize-none`} value={contenido}
                    onChange={e => setContenido(e.target.value)} placeholder="Escribe la nota..." />
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-2">Grabación de voz</label>
                    {audioBlob ? (
                      <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
                        <span className="text-sm text-purple-700">Audio grabado</span>
                        <button type="button" onClick={() => setAudioBlob(null)}
                          className="ml-auto text-xs text-red-500 hover:underline">Eliminar</button>
                      </div>
                    ) : (
                      <VoiceRecorder onStop={blob => setAudioBlob(blob)} />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Transcripción / notas adicionales</label>
                    <textarea rows={3} className={`${inputCls} resize-none`} value={contenido}
                      onChange={e => setContenido(e.target.value)} placeholder="Transcripción opcional..." />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-text-secondary mb-2">Archivos adjuntos</label>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleAdjunto} />
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 border border-dashed border-gray-300 text-text-secondary hover:border-gray-400 px-3 py-2 rounded-xl text-sm transition-colors w-full justify-center">
                  <Paperclip size={14} /> Adjuntar archivos
                </button>
                {adjuntos.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {adjuntos.map((f, i) => (
                      <div key={i} className="flex items-center gap-1 bg-gray-100 text-text-secondary px-2 py-1 rounded-lg text-xs">
                        {f.name}
                        <button type="button" onClick={() => setAdjuntos(prev => prev.filter((_, j) => j !== i))}>
                          <X size={10} className="text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={createMutation.isPending}
                  className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
                  {createMutation.isPending ? 'Guardando...' : 'Guardar nota'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
